/* eslint-disable class-methods-use-this */
import EventClass from "common/utils/ui/event-class";
import { track } from "jiffy-analytics";
import {
  detectDeliveryTypes,
  updateFilterDisplay,
  handleSameDayFilter,
  createBadgeObserver,
  observeProductCards,
  resetSameDayFilter,
  setActiveTab,
  setSectionVisibility,
  handleHashNavigation
} from "./delivery-filter-utils";

class DTFCategoryFilter extends EventClass {
  #$easyShoppingOriginalSibling;

  #sameDayFilterActive = false;

  #badgeObserver;

  #pendingReset = false;

  get #$sections() {
    return document.querySelectorAll(".js-dtf-category");
  }

  get #$sectionFillers() {
    return document.querySelectorAll(".js-dtf-category-filler");
  }

  get #$easyShopping() {
    return document.querySelector(".js-dtf-category-easy-shopping");
  }

  get #$tabHeader() {
    return document.querySelector(".js-dtf-category-header");
  }

  get #$mostPopularTab() {
    return this.#$tabHeader?.querySelector('[data-header-section="Most Popular"]');
  }

  get #$allProductsTab() {
    return this.#$tabHeader?.querySelector('[data-header-section="All Products"]');
  }

  get #$mostPopularSection() {
    return document.querySelector('[data-section="Most Popular"]');
  }

  get #$allOtherSections() {
    return document.querySelectorAll('[data-section]:not([data-section="Most Popular"])');
  }

  get #$sameDayFilterCheckbox() {
    return document.querySelector(".js-same-day-filter-checkbox");
  }

  get #$productCards() {
    return document.querySelectorAll(".js-products__item");
  }

  constructor() {
    super({ eventSelector: ".js-dtf-category-header" });
    const $easyShopping = this.#$easyShopping;
    const $sectionContainer = this.#$sections[0]?.parentElement;
    if ($easyShopping && $sectionContainer) {
      $sectionContainer.appendChild($easyShopping);
    }
    this.#handleHashBasedNavigation();
    this.#initSameDayFilter();
  }

  #initSameDayFilter() {
    // Initially detect and setup filter display
    this.#detectAndSetupDeliveryFilter();

    // Set up observer for future changes
    this.#observeBadgeChanges();

    const $checkbox = this.#$sameDayFilterCheckbox;
    if ($checkbox) {
      // Initialize filter as unchecked (no URL param support)
      this.#sameDayFilterActive = false;
      $checkbox.checked = false;

      $checkbox.addEventListener("change", () => {
        this.#sameDayFilterActive = $checkbox.checked;
        this.#handleSameDayFilter();
      });
    }
  }

  #observeBadgeChanges() {
    // Clean up existing observer before creating new one
    if (this.#badgeObserver) {
      this.#badgeObserver.disconnect();
    }

    // Get current visible product cards based on active category
    const $visibleCards = document.querySelectorAll(
      ".js-products__item:not([style*='display: none'])"
    );
    this.#badgeObserver = createBadgeObserver(() => {
      this.#detectAndSetupDeliveryFilter();
    });
    observeProductCards(
      $visibleCards.length ? $visibleCards : this.#$productCards,
      this.#badgeObserver
    );
  }

  #detectAndSetupDeliveryFilter() {
    // Get all product cards and let detectDeliveryTypes determine visibility
    const $allProducts = document.querySelectorAll(".js-products__item");
    const { hasSameDay, hasNextDay } = detectDeliveryTypes($allProducts);
    updateFilterDisplay(hasSameDay, hasNextDay);
  }

  #handleSameDayFilter() {
    handleSameDayFilter(this.#sameDayFilterActive, this.#$productCards);
  }

  #handleHashBasedNavigation() {
    handleHashNavigation(this.#$tabHeader, viewType => this.#showView(viewType));
  }

  #showView(viewType) {
    const isMostPopular = viewType === "most-popular";
    setActiveTab(viewType, this.#$mostPopularTab, this.#$allProductsTab);
    setSectionVisibility(
      { mostPopular: isMostPopular, others: !isMostPopular },
      this.#$mostPopularSection,
      this.#$allOtherSections
    );
    // Use requestAnimationFrame for DOM update synchronization
    requestAnimationFrame(() => this.#resetSameDayFilter());
  }

  /* eslint-disable max-lines-per-function */
  event(e, $header) {
    const { headerSection } = e.target.dataset;
    if ($header && headerSection) {
      track("dtf_category_selected", { category: headerSection });
      const isAllProducts = headerSection === "All Products";
      const $headingItems = Array.from($header.children);
      // Match the index of the heading item with the index of the section
      const $sections = [null, ...Array.from(this.#$sections)];
      const $sectionFillers = Array.from(this.#$sectionFillers);
      const $easyShopping = this.#$easyShopping;
      const $sectionContainer = this.#$sections[0].parentElement;

      $headingItems.forEach(($heading, index) => {
        $heading.classList.toggle("active", $heading.dataset.headerSection === headerSection);

        if (index > 0) {
          const $sectionHeader = $sections[index]?.querySelector(".js-products-header");
          if ($sectionHeader) {
            $sectionHeader.classList.toggle("hidden", !isAllProducts);
          }
          if ($sections[index]) {
            $sections[index].classList.toggle(
              "hidden",
              !(isAllProducts || $sections[index].dataset.section === headerSection)
            );
          }
        }

        // Move easy shopping to the correct position
        if (isAllProducts && $easyShopping) {
          if (index === 3) {
            $sections[index]?.appendChild($easyShopping);
          }
        } else {
          $sectionContainer?.appendChild($easyShopping);
        }
      });

      $sectionFillers.forEach($filler => {
        $filler.classList.toggle("hidden", !isAllProducts);
      });

      // Handle Most Popular section visibility
      const isMostPopular = headerSection === "Most Popular";
      const $mostPopularSection = document.querySelector('section[data-section="Most Popular"]');
      if ($mostPopularSection) {
        $mostPopularSection.classList.toggle("hidden", !isMostPopular);
      }

      // Reset the same day filter when changing categories after DOM updates
      requestAnimationFrame(() => this.#resetSameDayFilter());
    }
  }

  #resetSameDayFilter() {
    // Prevent concurrent resets
    if (this.#pendingReset) return;
    this.#pendingReset = true;

    const $checkbox = this.#$sameDayFilterCheckbox;
    const wasActive = this.#sameDayFilterActive;

    if ($checkbox && this.#sameDayFilterActive) {
      this.#sameDayFilterActive = false;
      $checkbox.checked = false;
      this.#handleSameDayFilter();
    }

    resetSameDayFilter({
      $checkbox,
      isActive: wasActive,
      observer: this.#badgeObserver,
      setupCallback: () => {
        this.#observeBadgeChanges();
        this.#detectAndSetupDeliveryFilter();
        this.#pendingReset = false;
      }
    });
  }
}

export default () => new DTFCategoryFilter();
